// @flow
import { useFilters } from 'hooks/useQuery'
import useScreenSizeStyle from 'hooks/useScreenSizeStyle'
import { useDeleteExports } from 'queries/exports/useDeleteExports'
import { useLoadExports } from 'queries/exports/useLoadExports'
import * as React from 'react'
import { Helmet } from 'react-helmet'
import { useHistory } from 'react-router-dom'
import ChevronDownMenu from 'ui/atoms/ChevronDownMenu'
import Pagination from 'ui/atoms/Pagination'
import Table from 'ui/atoms/Table'
import Text from 'ui/atoms/Text'
import Page from 'ui/organisms/Page'
import { getFormattedDate } from 'utils/dates'

import ExportsCreateModal from './components/ExportsCreateModal'
import ExportsPageHeader from './components/ExportsPageHeader'

function ExportsPage(): React.Node {
  const history = useHistory()
  const [selectedExport, setSelectedExport] = React.useState(null)
  const [showCreateModal, setShowCreateModal] = React.useState(false)
  const textSize = useScreenSizeStyle({
    md: { small: true, xsmall: false },
    generic: { small: false, xsmall: true },
  })
  const { deleteExport, isLoading: isDeleteLoading } = useDeleteExports()

  const { filters } = useFilters({ defaultValues: { page: 1, pageSize: 25 } })
  const {
    data,
    isLoading: isExportsLoading,
    isFetching,
  } = useLoadExports(filters)

  const isLoading = isExportsLoading || isFetching || isDeleteLoading

  const maxWidthText = useScreenSizeStyle({
    md: '170px',
    generic: '110px',
  })

  const handleExportClick = React.useCallback(
    (exportItem) => {
      history.push(`/exports/${exportItem.id}`)
    },
    [history]
  )

  return (
    <>
      <Helmet>
        <title>Exports/Zentake</title>
      </Helmet>

      <Page title="Exports">
        <ExportsPageHeader onCreateExport={setShowCreateModal} />

        <Table>
          <Table.Head
            columns={['EXPORT NAME', 'FORM NAME', 'DATE CREATED', '']}
          />

          <Table.Body>
            {isLoading && (
              <Table.Row>
                <Table.Column>
                  <Text>Loading...</Text>
                </Table.Column>
                <Table.Column />
                <Table.Column />
                <Table.Column />
              </Table.Row>
            )}

            {!isLoading && data?.results.length === 0 && (
              <Table.Row>
                <Table.Column>
                  <Text>No Results Found</Text>
                </Table.Column>
                <Table.Column />
                <Table.Column />
                <Table.Column />
              </Table.Row>
            )}

            {!isLoading &&
              data?.results.map((exportItem) => (
                <Table.Row
                  className="hover:bg-gray-50 border-b border-gray-200 hover:no-underline cursor-pointer"
                  key={exportItem.id}
                  onClick={() => handleExportClick(exportItem)}
                >
                  <Table.Column>
                    <Text
                      $truncate
                      $small={textSize.small}
                      $xsmall={textSize.xsmall}
                      style={{ maxWidth: maxWidthText }}
                    >
                      {exportItem.name}
                    </Text>
                  </Table.Column>
                  <Table.Column>
                    <Text
                      $truncate
                      $small={textSize.small}
                      $xsmall={textSize.xsmall}
                      style={{ maxWidth: maxWidthText }}
                    >
                      {exportItem.form.name}
                    </Text>
                  </Table.Column>
                  <Table.Column>
                    <Text
                      $small={textSize.small}
                      $xsmall={textSize.xsmall}
                      style={{ maxWidth: maxWidthText }}
                    >
                      {getFormattedDate(exportItem.created_at)}
                    </Text>
                  </Table.Column>
                  <Table.Column>
                    <ChevronDownMenu
                      options={[
                        {
                          label: 'Edit',
                          onClick: () => setSelectedExport(exportItem),
                        },
                        {
                          label: 'Delete',
                          onClick: () => {
                            deleteExport(exportItem.id)
                          },
                        },
                      ]}
                    />
                  </Table.Column>
                </Table.Row>
              ))}
          </Table.Body>
        </Table>
        <Pagination.Border>
          <Pagination total={data?.count || 0} />
        </Pagination.Border>
      </Page>
      {(showCreateModal || selectedExport) && (
        <ExportsCreateModal
          onClose={() => {
            setShowCreateModal(false)
            setSelectedExport(null)
          }}
          selectedExport={selectedExport}
        />
      )}
    </>
  )
}

export default ExportsPage
